import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft, CheckCircle2, Shield, Cpu, Network, Hexagon } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { AuthApiService } from '@/features/auth/api/authApi';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // DVD bouncing logo state
  const [dvdPosition, setDvdPosition] = useState({ x: 100, y: 100 });
  const [dvdVelocity, setDvdVelocity] = useState({ dx: 2, dy: 1.5 });
  const [dvdRotation, setDvdRotation] = useState(0);
  const [dvdLogoError, setDvdLogoError] = useState(false);
  const animationFrameRef = useRef<number>();
  const logoSize = 80;

  // DVD bouncing animation
  useEffect(() => {
    const animate = () => {
      setDvdPosition(prev => {
        let newX = prev.x + dvdVelocity.dx;
        let newY = prev.y + dvdVelocity.dy;
        let newDx = dvdVelocity.dx;
        let newDy = dvdVelocity.dy;

        if (newX <= 0 || newX >= window.innerWidth - logoSize) {
          newDx = -newDx;
          newX = Math.max(0, Math.min(newX, window.innerWidth - logoSize));
        }
        if (newY <= 0 || newY >= window.innerHeight - logoSize) {
          newDy = -newDy;
          newY = Math.max(0, Math.min(newY, window.innerHeight - logoSize));
        }

        if (newDx !== dvdVelocity.dx || newDy !== dvdVelocity.dy) {
          setDvdVelocity({ dx: newDx, dy: newDy });
        }

        return { x: newX, y: newY };
      });

      setDvdRotation(prev => (prev + 1) % 360);
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [dvdVelocity]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await AuthApiService.requestPasswordReset(email);
      setSuccess(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || t('shared.requestResetErrorFallback'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#1a1f2e] relative overflow-hidden flex items-center justify-center dark">
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

        {/* DVD-style bouncing logo */}
        <div
          className="pointer-events-none fixed z-10"
          style={{
            left: dvdPosition.x,
            top: dvdPosition.y,
            width: logoSize,
            height: logoSize,
            transform: `rotate(${dvdRotation}deg)`,
          }}
        >
          {dvdLogoError ? (
            <Hexagon className="w-full h-full text-cyan-400/20 drop-shadow-[0_0_10px_rgba(0,255,255,0.3)]" />
          ) : (
            <img
              src="/logo-invexis-neon-short.png"
              alt=""
              className="w-full h-full object-contain opacity-20 drop-shadow-[0_0_10px_rgba(0,255,255,0.3)]"
              onError={() => setDvdLogoError(true)}
            />
          )}
        </div>

        {/* Floating icons */}
        <div className="absolute top-20 left-20 text-cyan-400/20 animate-[float_6s_ease-in-out_infinite]">
          <Hexagon className="w-16 h-16" />
        </div>

        {/* Blurred background circles */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-400/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/5 rounded-full blur-[100px]" />

        {/* Card */}
        <div className="relative z-20 rounded-2xl p-8 md:p-12 max-w-md w-full mx-4 border border-white/10 bg-[#20263a]/95 animate-fade-in">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src="/logo-invexis-neon.png"
              alt="Invexis Logo"
              className="h-16 md:h-20 drop-shadow-[0_0_15px_rgba(0,255,255,0.5)]"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/logo-invexis.png';
                target.className = 'h-16 md:h-20';
              }}
            />
          </div>

          <div className="flex flex-col items-center mb-6">
            <CheckCircle2 className="h-16 w-16 text-emerald-400 mb-4 drop-shadow-[0_0_10px_rgba(52,211,153,0.4)]" />
            <h2 className="text-2xl font-bold text-white">{t('shared.emailSentTitle')}</h2>
            <p className="mt-2 text-sm text-gray-400 text-center">
              {t('shared.emailSentDescription')}
            </p>
          </div>

          <div className="space-y-4">
            <Alert className="bg-cyan-400/10 border-cyan-400/30">
              <AlertDescription className="text-cyan-300">
                {t('shared.checkInboxNotice')}
              </AlertDescription>
            </Alert>

            <Link to="/login" className="block">
              <Button
                variant="outline"
                className="w-full h-12 text-base font-semibold border-white/10 text-gray-300 hover:bg-white/5 hover:text-white"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('shared.backToLoginLink')}
              </Button>
            </Link>
          </div>
        </div>

        {/* Corner decorations */}
        <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-cyan-400/20 rounded-tl-3xl" />
        <div className="absolute top-0 right-0 w-32 h-32 border-r-2 border-t-2 border-primary/20 rounded-tr-3xl" />
        <div className="absolute bottom-0 left-0 w-32 h-32 border-l-2 border-b-2 border-primary/20 rounded-bl-3xl" />
        <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-cyan-400/20 rounded-br-3xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1f2e] relative overflow-hidden flex items-center justify-center dark">
      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

      {/* DVD-style bouncing logo */}
      <div
        className="pointer-events-none fixed z-10"
        style={{
          left: dvdPosition.x,
          top: dvdPosition.y,
          width: logoSize,
          height: logoSize,
          transform: `rotate(${dvdRotation}deg)`,
        }}
      >
        {dvdLogoError ? (
          <Hexagon className="w-full h-full text-cyan-400/20 drop-shadow-[0_0_10px_rgba(0,255,255,0.3)]" />
        ) : (
          <img
            src="/logo-invexis-neon-short.png"
            alt=""
            className="w-full h-full object-contain opacity-20 drop-shadow-[0_0_10px_rgba(0,255,255,0.3)]"
            onError={() => setDvdLogoError(true)}
          />
        )}
      </div>

      {/* Floating icons */}
      <div className="absolute top-20 left-20 text-cyan-400/20 animate-[float_6s_ease-in-out_infinite]">
        <Hexagon className="w-16 h-16" />
      </div>
      <div className="absolute top-40 right-32 text-primary/20 animate-[float_8s_ease-in-out_infinite_1s]">
        <Shield className="w-12 h-12" />
      </div>
      <div className="absolute bottom-32 left-40 text-cyan-400/15 animate-[float_7s_ease-in-out_infinite_0.5s]">
        <Cpu className="w-14 h-14" />
      </div>
      <div className="absolute bottom-20 right-20 text-primary/15 animate-[float_9s_ease-in-out_infinite_1.5s]">
        <Network className="w-20 h-20" />
      </div>

      {/* Blurred background circles */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-400/5 rounded-full blur-[100px]" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/5 rounded-full blur-[100px]" />

      {/* Card */}
      <div className="relative z-20 rounded-2xl p-8 md:p-12 max-w-md w-full mx-4 border border-white/10 bg-[#20263a]/95 animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="/logo-invexis-neon.png"
            alt="Invexis Logo"
            className="h-16 md:h-20 drop-shadow-[0_0_15px_rgba(0,255,255,0.5)]"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/logo-invexis.png';
              target.className = 'h-16 md:h-20';
            }}
          />
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">{t('shared.forgotPasswordQuestion')}</h2>
          <p className="text-gray-400 text-sm">
            {t('shared.forgotPasswordInstructions')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 text-red-400">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-gray-300">
              {t('login.emailLabel')}
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
              <Input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('shared.emailPlaceholder')}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:ring-cyan-400/20"
                required
                autoComplete="email"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-cyan-500 to-primary hover:from-cyan-400 hover:to-primary/90 text-background shadow-lg shadow-cyan-500/25 transition-all duration-300 hover:shadow-cyan-500/40"
            disabled={!email.trim() || isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-background"></div>
                <span>{t('shared.sendingLabel')}</span>
              </div>
            ) : (
              t('shared.sendRecoveryEmailButton')
            )}
          </Button>

          <div className="text-center">
            <Link to="/login" className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors">
              <ArrowLeft className="inline-block mr-1 h-3 w-3" />
              {t('shared.backToLoginLink')}
            </Link>
          </div>
        </form>

        {/* Version */}
        <p className="text-center text-xs text-gray-500 mt-8">
          {t('shared.copyright', { year: new Date().getFullYear() })}
        </p>
      </div>

      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-cyan-400/20 rounded-tl-3xl" />
      <div className="absolute top-0 right-0 w-32 h-32 border-r-2 border-t-2 border-primary/20 rounded-tr-3xl" />
      <div className="absolute bottom-0 left-0 w-32 h-32 border-l-2 border-b-2 border-primary/20 rounded-bl-3xl" />
      <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-cyan-400/20 rounded-br-3xl" />
    </div>
  );
};

export default ForgotPassword;
